import { inject, Signal } from '@angular/core';

import { injectQuery } from '@tanstack/angular-query-experimental';

import { NomObjectClass } from 'src/app/core/constants';

import { UtilsLogApiService } from '../services/utils-log-api.service';

import { firstValueFrom } from 'rxjs';

/**
 * Query key + fetcher for a user action log's extra metadata, for consumers that
 * resolve the id at interaction time (e.g. the JSON viewer dialog).
 */
export function userActionLogExtraMetadataQuerySource(
  utilsLogApi: UtilsLogApiService,
  userActionLogId: string | number,
) {
  const id = userActionLogId?.toString() ?? '';

  return {
    queryKey: ['utils_log', 'get_user_action_log_extra_metadata', id],
    // ! resolves to null, never undefined — Angular Query rejects undefined data
    fetch: () =>
      firstValueFrom(
        utilsLogApi.getUserActionLogExtraMetadata({
          user_action_log_id: id,
        }),
      ).then((response) => response?.extra_metadata ?? null),
  };
}

/**
 * Saved-query history of a feed (GET /utils_log/get_query_history_inline) - shown by
 * the query-editor dialog and the search builder.
 */
export function injectFeedHistoryQuery(feedId: Signal<string | null>) {
  const utilsLogApi = inject(UtilsLogApiService);

  return injectQuery(() => ({
    queryKey: ['utils_log', 'get_query_history_inline', feedId()],
    queryFn: () =>
      firstValueFrom(utilsLogApi.getFeedHistory(feedId(), NomObjectClass.FEED)),
    enabled: !!feedId(),
  }));
}

export interface OutputLogsScope {
  objectId: string;
  objectClassId: number;
}

/** Export/output logs of an object (GET /utils_log/get_output_logs). */
export function injectOutputLogsQuery(scope: Signal<OutputLogsScope | null>) {
  const utilsLogApi = inject(UtilsLogApiService);

  return injectQuery(() => ({
    queryKey: [
      'utils_log',
      'get_output_logs',
      { objectId: scope()?.objectId, objectClassId: scope()?.objectClassId },
    ],
    queryFn: () =>
      firstValueFrom(
        utilsLogApi.getOutputLogs(scope().objectId, scope().objectClassId),
      ),
    enabled: !!scope(),
  }));
}
